import _ from "lodash";
import { Form } from "formik";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { Button2 } from "@/ui2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { CustomForm } from "@/component-library";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import dayjs from "@/lib/helpers/dayjs";
import { Divider2 } from "@/ui2/atoms/Divider";
import { filterTimePickerOptionsByLabel } from "@/features/calendar/utils/dates";
import { FormDateField } from "@/component-library/FormFields/FormDateField/FormDateField";
import { FormMultiSelect } from "@/component-library/FormFields/FormMultiSelect/FormMultiSelect";
import { FormRadioGroup } from "@/component-library/FormFields/FormRadioGroup/FormRadioGroup";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { providerRequests } from "@/api/(requests)/provider";
import { ScrollToFormError } from "@/component-library/ScrollToFormError/ScrollToError";
import { showToast } from "@/providers/ToastProvider";
import { testLocator } from "@/lib/helpers/test-locator";

import {
  ACTIVITY_CLASS_OPTIONS,
  ACTIVITY_PURPOSE_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
  ACTIVITY_SEVICE_TYPE_OPTIONS,
  CARE_TEAM_MEMBER_TYPE_OPTIONS,
  COMMUNICATION_TYPE_OPTIONS,
  CONDITIONAL_FIELD_CLEARING_RULES,
  INTENDED_DURATION_OPTIONS,
  REASON_FOR_COMMUNICATION_OPTIONS,
  RECOMMENDED_NEXT_OPTIONS,
  YES_NO_OPTIONS,
  filterDurationOptionsByLabel,
} from "../constants";
import { adaptRadioFields } from "../helpers/adapters";
import { CredentialField } from "../../components/CredentialField";
import { getTimeSlotOptionsWithValue } from "../helpers/time-slots";
import { ProviderSignatureField } from "../../components/ProviderSignatureField";

import { ADVANCED_CARE_ACTIVITY_SCHEMA } from "./AdvancedCareActivity.schema";
import { AdvancedCareActivityTextArea } from "./AdvancedCareActivityTextArea";
import { AdvancedCareActivityTextInput } from "./AdvancedCareActivityInput";

export function AdvancedCareActivityForm({
  isEditing,
  isEmbedded = false,
  nameToSign,
  noteState,
  onSubmitSuccess,
  patientId,
  refetchNote = undefined,
  setIsEditing = undefined,
}) {
  const history = useHistory();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const preventEnterFormSubmission = (event) => {
    if (event.key === "Enter" || event.code === "Enter") {
      event.preventDefault();
    }
  };

  const renderForm = (formikProps) => {
    // reset the form when it enters view only mode and is dirty or the note
    // has been updated and the form values need to be refreshed — also
    // clears any stale errors/touched left over from a prior failed submit
    // attempt (e.g. Save Draft after a failed Sign & Submit)
    if (!isEditing && (formikProps.dirty || !_.isEqual(noteState, formikProps.values))) {
      // pass only `values` — omitting touched/errors lets Formik default them
      // to {}, actually clearing stale validation state instead of carrying
      // it forward (passing formikProps itself would re-apply the same stale
      // touched/errors verbatim)
      formikProps.resetForm({ values: noteState });
    }

    return (
      <Form
        className="tw:grid! tw:items-start tw:gap-8 tw:max-lg:grid-cols-1 tw:lg:grid-cols-2 tw:print:grid-cols-2!"
        key={!isEditing}
        onKeyDown={preventEnterFormSubmission}
      >
        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          formikProps={formikProps}
          name="content.activity_service_type"
          options={ACTIVITY_SEVICE_TYPE_OPTIONS}
          placeholder="Choose most relevant"
          testId="advancedCareActivityActivityServiceTypeField"
          title="Advanced Care Service"
          triggerErrorOnChange
        />

        <div className="tw:flex tw:flex-col tw:gap-5">
          <FormMultiSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.activity_purpose"
            options={ACTIVITY_PURPOSE_OPTIONS}
            placeholder="Search Activity Type"
            testId="advancedCareActivityActivityPurposeField"
            title="Activity Type"
            triggerErrorOnChange
          />

          {formikProps.values.content.activity_purpose?.includes("Other") && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.activity_purpose_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityActivityPurposeOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterDurationOptionsByLabel}
          formikProps={formikProps}
          name="content.intended_duration"
          options={INTENDED_DURATION_OPTIONS}
          placeholder="Anticipated duration of the activity"
          testId="advancedCareActivityDurationField"
          title="Intended Duration"
          triggerErrorOnChange
        />

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="service_location"
          options={ACTIVITY_CLASS_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityLocationField"
          title="Activity Location"
          triggerErrorOnChange
        />

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.communication_type"
            options={COMMUNICATION_TYPE_OPTIONS}
            placeholder="Search Communication Method"
            testId="advancedCareActivityCommunicationTypeField"
            title="Communication Method"
            triggerErrorOnChange
          />

          {formikProps.values.content.communication_type === "Other" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.communication_type_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityCommunicationTypeOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.activity_reason"
          options={ACTIVITY_REASON_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityActivityReasonField"
          title="Was the activity planned?"
          triggerErrorOnChange
        />

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.patient_encounter"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityEncounterField"
          title="Was the patient expected to be present?"
          triggerErrorOnChange
        />

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.patient_participant_attest"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityAttendField"
          title="Did the patient attend?"
          triggerErrorOnChange
        />

        <FormDateField
          disabled={!isEditing}
          formikProps={formikProps}
          inputClassName={!isEditing && "AdvancedCareActivityForm-input_read_only"}
          name="session_date"
          placeholder="MM/DD/YYYY"
          testId="advancedCareActivityDateField"
          title="Activity Date"
          triggerErrorOnChange
          {...getAutomationDataAttr("advanced-care-activity-assessment-date-field")}
        />

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterTimePickerOptionsByLabel}
          formikProps={formikProps}
          name="session_from"
          options={getTimeSlotOptionsWithValue(formikProps.values.session_from)}
          otherErrorNames={["time_error", "start_date_time_error"]}
          placeholder="HH:MM"
          testId="advancedCareActivityStartField"
          title="Start Time"
          triggerErrorOnChange
        />

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterTimePickerOptionsByLabel}
          formikProps={formikProps}
          name="session_to"
          options={getTimeSlotOptionsWithValue(formikProps.values.session_to)}
          otherErrorNames={["time_error", "end_date_time_error"]}
          placeholder="HH:MM"
          testId="advancedCareActivityEndField"
          title="End Time"
          triggerErrorOnChange
        />

        <FormMultiSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          formikProps={formikProps}
          name="content.reason_for_communication"
          options={REASON_FOR_COMMUNICATION_OPTIONS}
          placeholder="Search Reason for Communication"
          testId="advancedCareActivityReasonForCommunicationField"
          title="Reason for Communication"
          triggerErrorOnChange
        />

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormRadioGroup
            className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
            disabled={!isEditing}
            formikProps={formikProps}
            name="content.activity_result"
            options={YES_NO_OPTIONS}
            radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
            radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
            radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
            testId="advancedCareActivityResultField"
            title="Was the activity completed as planned?"
            triggerErrorOnChange
          />

          {formikProps.values.content.activity_result === "No" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.activity_result_note"
              readOnly={!isEditing}
              testId="advancedCareActivityResultNoteField"
              title="Why was the activity not completed as planned?"
              triggerErrorOnChange
            />
          )}
        </div>

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormMultiSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.recommended_next"
            options={RECOMMENDED_NEXT_OPTIONS}
            placeholder="Search Care team follow up"
            testId="advancedCareActivityRecommendedNextField"
            title="Care Team Follow Up"
            triggerErrorOnChange
          />

          {formikProps.values.content.recommended_next?.includes("Other") && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.recommended_next_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityRecommendedNextOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        <Divider2 className="tw:lg:col-span-2 tw:print:col-span-2" />

        {/* ACUTE EPISODE */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.acute_episode"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityAcuteEpisode"
          title="ED visit or inpatient care for BH within 6 months?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.acute_episode ||
            formikProps.values.content.acute_episode === "No"
          }
          name="content.acute_episode_details"
          placeholder="Please provide details"
          readOnly={!isEditing}
          testId="advancedCareActivityAcuteEpisodeDetails"
          title="Details"
          triggerErrorOnChange
        />

        {/* ACTIVE SUBSTANCE */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.active_substance"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityActiveSubstance"
          title="Active substance use disorder?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.active_substance ||
            formikProps.values.content.active_substance === "No"
          }
          name="content.active_substance_details"
          placeholder="Include type of substance and frequency if comfortable sharing"
          readOnly={!isEditing}
          testId="advancedCareActivityActiveSubstanceDetails"
          title="Details"
          triggerErrorOnChange
        />

        {/* SDOH */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.sdoh"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivitySdoh"
          title="Housing, Financial, or Safety Concerns (SDOH)?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={!formikProps.values.content.sdoh || formikProps.values.content.sdoh === "No"}
          name="content.sdoh_details"
          placeholder="Please provide details"
          readOnly={!isEditing}
          testId="advancedCareActivitySdohDetails"
          title="Details"
          triggerErrorOnChange
        />

        {/* DAILY ACTIVITIES */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.activity_difficulties"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityDailyActivities"
          title="Difficulty with Activities of Daily Living (ADLs)?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.activity_difficulties ||
            formikProps.values.content.activity_difficulties === "No"
          }
          name="content.activity_difficulties_details"
          placeholder="Please provide details (cleaning, cooking, toileting, etc.)"
          readOnly={!isEditing}
          testId="advancedCareActivityDailyActivitiesDetails"
          title="Details"
          triggerErrorOnChange
        />

        <Divider2 className="tw:lg:col-span-2 tw:print:col-span-2" />

        {/* SUMMARY */}
        <div className="tw:lg:col-span-2 tw:print:col-span-2">
          <AdvancedCareActivityTextArea
            className="AdvancedCareActivityForm-summary_form_field"
            formikProps={formikProps}
            inputClassName="AdvancedCareActivityForm-summary_form_field_input"
            name="content.additional_information"
            placeholder="Highlight interventions and patient reactions"
            readOnly={!isEditing}
            testId="advancedCareActivitySummaryField"
            title="Summary"
            triggerErrorOnChange
          />
        </div>

        {/* YOUR ROLE */}
        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.care_team_member_type"
            options={CARE_TEAM_MEMBER_TYPE_OPTIONS}
            placeholder="Search Role"
            testId="advancedCareActivityRoleField"
            title="Your Role"
            triggerErrorOnChange
          />

          {formikProps.values.content.care_team_member_type === "Other" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.care_team_member_type_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityRoleOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        {/* SIGNATURE/DATE/CREDENTIALS */}
        <div className="tw:col-start-1 tw:lg:col-span-2 tw:lg:flex tw:lg:justify-between tw:print:col-span-2">
          {isEditing && (
            <ProviderSignatureField
              formikProps={formikProps}
              isViewOnly={false}
              name="signature"
              nameToSign={nameToSign}
              testId="advancedCareActivitySignature"
            />
          )}

          {/* CREDENTIALS */}
          {isEditing && (
            <>
              {formikProps.values["provider_cp_credentials"] && (
                <CredentialField
                  formikProps={formikProps}
                  isViewOnly={false}
                  name="provider_cp_credentials"
                />
              )}
            </>
          )}

          <ScrollToFormError />
        </div>

        {/* SUBMIT */}
        {isEditing && (
          <div className="tw:flex tw:gap-3 tw:lg:col-start-2 tw:lg:ml-auto!">
            <Button2
              className="tw:min-w-40"
              isDisabled={formikProps.isSubmitting}
              isLoading={isSavingDraft}
              onPress={() => saveDraft(formikProps.values)}
              size="md"
              variant="secondary"
              {...getAutomationDataAttr("advanced-care-activity-assessment-save-draft-button")}
              {...testLocator("advancedCareActivitySaveDraftBtn")}
            >
              Save Draft
            </Button2>

            <Button2
              className="tw:min-w-40"
              isDisabled={isSavingDraft}
              isLoading={formikProps.isSubmitting}
              size="md"
              type="submit"
              {...getAutomationDataAttr("advanced-care-activity-assessment-submit-button")}
              {...testLocator("advancedCareActivitySubmitBtn")}
            >
              Sign & Submit
            </Button2>
          </div>
        )}
      </Form>
    );
  };

  // Shared payload prep for both Save Draft and Sign & Submit: clears
  // conditional details fields, casts legacy "Yes"/"No" radio values to
  // booleans, and normalizes date/duration fields. Draft saves may have
  // unanswered fields, so boolean casting falls back to null rather than
  // throwing.
  const buildSubmissionPayload = (formData) => {
    const copyOfFormData = _.cloneDeep(formData);
    const { content } = copyOfFormData;

    CONDITIONAL_FIELD_CLEARING_RULES.forEach(({ field, shouldClear }) => {
      if (shouldClear(content)) {
        copyOfFormData.content[field] = "";
      }
    });

    copyOfFormData.content = adaptRadioFields(copyOfFormData.content);

    if (copyOfFormData["session_date"]) {
      copyOfFormData["session_date"] = dayjs(copyOfFormData["session_date"], "MMDDYYYY").format(
        "MM/DD/YYYY",
      );
    }

    if (copyOfFormData["content"]["intended_duration"]) {
      copyOfFormData["content"]["intended_duration"] = Number(
        copyOfFormData["content"]["intended_duration"],
      );
    }

    return copyOfFormData;
  };

  // A note that already exists (created via the two-step video-call flow,
  // always in "draft" state) is updated via PUT; a brand new note (manual
  // "New Note" entry point) is created via POST — mirrors CareManagementForm.
  const submitNote = async (formData) => {
    const urlParams = { patientId };

    if (noteState.note_state === "draft") {
      await providerRequests.put_progress_note({ data: formData, urlParams });
    } else {
      await providerRequests.post_progress_note({ data: formData, urlParams });
    }
  };

  const handlePostSubmitNavigation = () => {
    if (onSubmitSuccess) {
      onSubmitSuccess();

      return;
    }

    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  const onSubmit = async (formData) => {
    try {
      const copyOfFormData = buildSubmissionPayload(formData);

      copyOfFormData["completed_date_time"] = dayjs().format();
      copyOfFormData["note_state"] = "complete";

      await submitNote(copyOfFormData);
      handlePostSubmitNavigation();
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Failed to submit note" }), {
        tags: {
          component: "AdvancedCareActivityForm",
          errorType: "submit_note",
          patientId,
        },
      });
      showToast([{ heading: "Failed to submit note.", variant: "error" }]);
    }
  };

  // Navigates away on the note's first save (POST, brand new note) same as
  // CareManagementForm. Re-saving an already-draft note via PUT also
  // navigates when a consumer passed onSubmitSuccess (video-call step 2 —
  // provider should return to the dashboard after every draft save); when
  // embedded in the standard note view (NoteContentRouter/NoteViewV2)
  // instead, no onSubmitSuccess is passed, so it refetches and closes back
  // to the read-only view showing the freshly-saved draft.
  const saveDraft = async (formikValues) => {
    setIsSavingDraft(true);

    try {
      const copyOfFormData = buildSubmissionPayload(formikValues);
      const isUpdatingExistingDraft = noteState.note_state === "draft";

      copyOfFormData["note_state"] = "draft";

      await submitNote(copyOfFormData);

      showToast([{ heading: "Draft saved.", variant: "success" }]);

      if (!isUpdatingExistingDraft || onSubmitSuccess) {
        handlePostSubmitNavigation();
      } else if (refetchNote && setIsEditing) {
        await refetchNote();
        setIsEditing(false);
      }
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Failed to save draft" }), {
        tags: {
          component: "AdvancedCareActivityForm",
          errorType: "save_draft",
          patientId,
        },
      });
      showToast([{ heading: "Failed to save draft.", variant: "error" }]);
    } finally {
      setIsSavingDraft(false);
    }
  };

  if (isEmbedded) {
    return noteState ? (
      <CustomForm
        initialValues={noteState}
        onSubmit={onSubmit}
        render={renderForm}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={ADVANCED_CARE_ACTIVITY_SCHEMA}
      />
    ) : null;
  }

  return (
    <DashboardCard2
      rawChildren
      title="Advanced Care Activity Assessment"
      {...getAutomationDataAttr("advanced-care-activity-assessment-title")}
    >
      {noteState && (
        <CustomForm
          initialValues={noteState}
          onSubmit={onSubmit}
          render={renderForm}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={ADVANCED_CARE_ACTIVITY_SCHEMA}
        />
      )}
    </DashboardCard2>
  );
}
